import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import NotFoundPage from "@/pages/NotFoundPage";
import Breadcrumbs from "@/components/cms/Breadcrumbs";
import RichBody, { Toc, FaqList, useRichBody } from "@/components/cms/RichBody";
import { RelatedProducts, LinkCards, ArticleCard } from "@/components/cms/Related";
import { Button } from "@/components/ui/button";

export function ArticlesList() {
  const { cat } = useParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  useEffect(() => { api.get("/article-categories").then((r) => setCats(r.data.items)); }, []);
  useEffect(() => { setSkip(0); }, [cat]);
  useEffect(() => {
    api.get("/articles", { params: { category: cat || "", skip, limit: 12 } })
      .then((r) => setData((d) => (skip && d ? { ...r.data, items: [...d.items, ...r.data.items] } : r.data)));
  }, [cat, skip]);
  const current = cats.find((c) => c.slug === cat);
  const title = current ? `${current.name} Articles` : "Articles & Buying Guides";
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="articles-page">
      <Header />
      <Seo title={`${title} | ACP Online`} description="Practical guides on POS systems, EFTPOS, cash registers, CCTV, networking and retail technology for New Zealand businesses."
        canonical={cat ? `/articles/category/${cat}` : "/articles"} />
      <section className="bg-steel text-white"><div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Articles", path: "/articles" }, ...(current ? [{ name: current.name, path: `/articles/category/${cat}` }] : [])]} />
        <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold" data-testid="articles-h1">{title}</h1>
      </div></section>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex-1 space-y-6">
        <div className="flex flex-wrap gap-2" data-testid="article-cats">
          <Link to="/articles" className={`px-3 py-1.5 rounded-full text-sm border ${!cat ? "bg-steel text-white" : "bg-white hover:border-accent"}`}>All</Link>
          {cats.filter((c) => c.count > 0).map((c) => <Link key={c.slug} to={`/articles/category/${c.slug}`} className={`px-3 py-1.5 rounded-full text-sm border ${cat === c.slug ? "bg-steel text-white" : "bg-white hover:border-accent"}`}>{c.name}</Link>)}
        </div>
        {!data ? <p className="text-sm text-slate-500">Loading…</p> : data.items.length === 0 ? <p className="text-slate-600" data-testid="articles-empty">No articles published yet.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.items.map((a) => <ArticleCard key={a.id} a={a} />)}</div>)}
        {data && data.items.length < data.total && <Button variant="outline" onClick={() => setSkip(data.items.length)} data-testid="articles-more">Load more</Button>}
      </main>
      <Footer />
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`, { params: { preview: sp.get("preview") || "" } })
      .then((r) => (r.data.redirect ? navigate(r.data.redirect, { replace: true }) : setData(r.data))).catch(() => setData(false));
  }, [slug, sp, navigate]);
  const { toc } = useRichBody(data?.doc?.body);
  if (data === false) return <NotFoundPage />;
  const a = data?.doc;
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="article-page">
      <Header />
      {!a ? <p className="p-20 text-center text-slate-500 flex-1">Loading…</p> : <>
        <Seo title={data.seo_title} description={data.meta_description} canonical={data.canonical} image={a.image} type="article" jsonLd={data.jsonld} noindex={!!a.noindex || !!sp.get("preview")} />
        <section className="bg-steel text-white"><div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Articles", path: "/articles" }, ...(data.category ? [{ name: data.category.name, path: `/articles/category/${data.category.slug}` }] : [])]} />
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight" data-testid="article-h1">{a.h1 || a.title}</h1>
          <p className="mt-3 text-sm text-slate-300" data-testid="article-meta">By {a.author || "ACP Online"}{data.published_nz ? ` · ${data.published_nz}` : ""}{data.updated_nz && data.updated_nz !== data.published_nz ? ` · Updated ${data.updated_nz}` : ""}</p>
        </div></section>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full flex-1 grid lg:grid-cols-4 gap-8">
          <article className="lg:col-span-3 space-y-8 min-w-0">
            {a.image && <img src={a.image} alt={a.image_alt || a.title} className="w-full rounded-xl aspect-[16/9] object-cover" />}
            {a.video_url && <a href={a.video_url} target="_blank" rel="noopener noreferrer" className="text-accent underline text-sm" data-testid="article-video">Watch the video</a>}
            <RichBody html={a.body} testid="article-body" />
            <FaqList faq={a.faq} />
            <LinkCards items={data.services} title="Related services" testid="article-services" />
            <RelatedProducts products={data.products} title="Products mentioned" />
            {data.related?.length > 0 && <section><h2 className="font-heading font-bold text-xl text-steel mb-3">More articles</h2>
              <div className="grid sm:grid-cols-3 gap-3">{data.related.map((r) => <ArticleCard key={r.id} a={r} />)}</div></section>}
          </article>
          <aside className="space-y-4 lg:sticky lg:top-24 self-start"><Toc toc={toc} />
            <Link to="/help/contact" className="block rounded-xl bg-accent text-steel font-semibold p-4 text-center hover:bg-amber-500" data-testid="article-cta">Talk to ACP Online</Link>
          </aside>
        </main>
      </>}
      <Footer />
    </div>
  );
}
